import * as React from 'react'

import type { CustomField } from 'modules/employees/pages/EditInBulk/types'
import type { ColumnNames } from 'modules/employees/pages/EditInBulk/vars'

// key is the name of the column, and number[] is the list
// of ids of selected employees for each column
export type TableSelection = Record<ColumnNames, number[]>

const emptyTableSelection = (customFields: CustomField[]): TableSelection => {
  const customFieldsIds = customFields.map((customField) => customField.id)

  return {
    employee: [],
    manager: [],
    office: [],
    regular_access_starts_on: [],
    time_off_supervisor: [],
    team: [],
    permission_group: [],
    legal_entity_and_employee_group: [],
    ...customFieldsIds.reduce((acc, customFieldId) => {
      return { ...acc, [customFieldId]: [] }
    }, {}),
  }
}

export default function useTableSelection({ customFields }: { customFields: CustomField[] }) {
  const [tableSelection, setTableSelection] = React.useState<TableSelection>(
    emptyTableSelection(customFields)
  )

  const clearTableSelection = () => {
    setTableSelection(emptyTableSelection(customFields))
  }

  const onSelect = (column: ColumnNames, selectedId: number) => {
    setTableSelection((prevState) => {
      const columnPrevState = prevState[column] || []
      let columnValues = columnPrevState

      if (columnPrevState.includes(selectedId)) {
        columnValues = columnValues.filter((value) => value !== selectedId)
      } else {
        columnValues = [...columnValues, selectedId]
      }

      return { ...prevState, [column]: columnValues }
    })
  }

  const onAllSelect = (column: ColumnNames, selectedIds: number[]) => {
    setTableSelection((prevState) => {
      const columnPrevState = prevState[column]
      const checked = selectedIds?.length === columnPrevState?.length

      return { ...prevState, [column]: checked ? [] : selectedIds }
    })
  }

  return {
    tableSelection,
    setTableSelection,
    clearTableSelection,
    onSelect,
    onAllSelect,
  }
}
